import React from 'react';

import { Badge, type BadgeProps } from '@/design-system/badge';
import {
  ITEM_CONDITION_LABELS,
  ITEM_STATUS_LABELS,
  SHIPMENT_STATUS_LABELS,
  type ItemCondition,
  type ItemStatus,
  type ShipmentStatus,
} from '@/services/api/status';

type BadgeStatus = NonNullable<BadgeProps['status']>;

/**
 * Shipment status → kit Badge status. Shipment status is a **rollup** of its
 * items, so the palette reads as custody → plan → motion → outcome:
 *   CREATED              → information (intake done, nothing at the hub yet)
 *   AT_HUB               → verified    (sky — in custody, nothing scheduled)
 *   PARTIALLY_DISPATCHED → stable      (teal — some items scheduled)
 *   DISPATCHED           → feature     (brand purple — all items scheduled)
 *   IN_DELIVERY          → away        (amber — out with a rider)
 *   IN_DISPUTE           → highlighted (transient: something is coming back)
 *   COMPLETED            → success     (terminal-good)
 *   RETURNED             → error       (terminal, everything came back)
 *   FAILED               → error       (terminal, nothing active left and some LOST)
 *   CANCELLED            → neutral     (terminal, low-noise)
 *
 * All ten are live values — `PARTIALLY_DISPATCHED` / `DISPATCHED` are what a
 * dispatch commit rolls the AWB into, so they get their own tones rather than
 * borrowing `AT_HUB`'s.
 *
 * Lifted out of `pages/shipments/index.tsx` so both the list table and the
 * detail panel can share — and so the panel can import it without pulling
 * the whole list page in (which would close a circular dep loop).
 */
export const SHIPMENT_STATUS_TONE: Record<ShipmentStatus, BadgeStatus> = {
  CREATED: 'information',
  AT_HUB: 'verified',
  PARTIALLY_DISPATCHED: 'stable',
  DISPATCHED: 'feature',
  IN_DELIVERY: 'away',
  IN_DISPUTE: 'highlighted',
  COMPLETED: 'success',
  RETURNED: 'error',
  CANCELLED: 'neutral',
  FAILED: 'error',
};

export const ShipmentStatusBadge: React.FC<{ status: ShipmentStatus }> = ({
  status,
}) => (
  <Badge status={SHIPMENT_STATUS_TONE[status] ?? 'neutral'} type="dot">
    {SHIPMENT_STATUS_LABELS[status] ?? status}
  </Badge>
);

/**
 * Item-level status — used in the items table inside the detail panel. The
 * scanned states lean `verified` (sky, operational midpoint — not a success
 * yet); the retur branch gets warning → error so a returning parcel never
 * looks like a delivered one.
 */
export const ITEM_STATUS_TONE: Record<ItemStatus, BadgeStatus> = {
  CREATED: 'information',
  HUB_SCANNED: 'verified',
  // Scheduled but physically still at the hub — matches the shipment tone.
  DISPATCHED: 'feature',
  HANDOVER_SCANNED: 'verified',
  IN_DELIVERY: 'away',
  COMPLETED: 'success',
  IN_RETURN: 'warning',
  RETURNED_TO_HUB: 'error',
  RETURNED_TO_CLIENT: 'error',
  CANCELLED: 'neutral',
  LOST: 'error',
  // Legacy (pre-migration rows only — `DELIVERED`/`RETURNED` were renamed and
  // `DAMAGED` became `condition`)
  DELIVERED: 'success',
  RETURNED: 'error',
  DAMAGED: 'warning',
  FAILED: 'error',
};

export const ItemStatusBadge: React.FC<{ status: ItemStatus }> = ({
  status,
}) => (
  <Badge status={ITEM_STATUS_TONE[status] ?? 'neutral'} size="sm">
    {ITEM_STATUS_LABELS[status] ?? status}
  </Badge>
);

/**
 * Condition flag — the v2 attribute that replaced item status `DAMAGED`. Shown
 * *next to* the status badge, never instead of it: a damaged parcel still has a
 * real custody status, and the flag rides along to the end of the journey.
 * Renders nothing for `OK` / unset.
 */
export const ItemConditionBadge: React.FC<{
  condition?: ItemCondition | null;
}> = ({ condition }) =>
  condition === 'DAMAGED' ? (
    <Badge
      status="warning"
      size="sm"
      type="left-icon"
      icon={<i className="ri-alert-line" aria-hidden="true" />}
    >
      {ITEM_CONDITION_LABELS.DAMAGED}
    </Badge>
  ) : null;
